import { createContext, useContext } from "react";
import type { GeoObject, Geometry } from "@/types/api-v2";
import type { ReferenceLayerUiState } from "@/types/reference-layer";

export type SidebarTab = "objects" | "details" | "layers" | "assistant";

export interface MvtFilters {
  objectTypes: number[];
  statuses: string[];
  organizations: number[];
  geometryPrimitiveCodes: string[];
}

export interface MvtLayerSettings {
  showPoints: boolean;
  showLines: boolean;
  showPolygons: boolean;
  pointOpacity: number;
  lineOpacity: number;
  polygonOpacity: number;
  /** Wave 1.5: только KLS-значки (без fill/line/circle). */
  symbolOnlyMode: boolean;
  /** Wave 1.6: подписи с leader-line (MapLibre MVP). */
  calloutsEnabled: boolean;
  /**
   * Exclusive-режим: только значки объектов + подписи, без геометрий и без кластеров.
   * При включении отменяет обычный MVT/cluster/callouts UX.
   */
  iconsLabelsMode: boolean;
}

export interface MapUiContextType {
  selectedObject: GeoObject | null;
  /**
   * Выбор объекта (карта, список, MVT).
   * `focusGeometryId` — подсветка геометрии и query в URL.
   * `openSidebarDetails` — false: только выбор и центрирование на карте, вкладка «Объекты» (одиночный клик в списке).
   */
  selectObject: (
    object: GeoObject,
    options?: { focusGeometryId?: number | null; openSidebarDetails?: boolean },
  ) => void;
  clearSelectedObject: () => void;

  /** Режим массового выбора (Sidebar + карта Shift+click/box). */
  multiSelectMode: boolean;
  setMultiSelectMode: (enabled: boolean) => void;
  /** Id объектов в multi-selection (sorted). */
  multiSelectedObjectIds: number[];
  toggleMultiSelectedObject: (objectId: number) => void;
  setMultiSelectedObjectIds: (ids: number[]) => void;
  addMultiSelectedObjectIds: (ids: number[]) => void;
  clearMultiSelection: () => void;

  sidebarTab: SidebarTab;
  setSidebarTab: (tab: SidebarTab) => void;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;

  activeTool: string | null;
  setActiveTool: (tool: string | null) => void;

  isDrawing: boolean;
  currentDrawing: any;
  currentObjectId: number | null;

  selectedGeometry: Geometry | null;
  setSelectedGeometry: (geometry: Geometry | null) => void;
  focusedGeometryId: number | null;
  setFocusedGeometryId: (geometryId: number | null) => void;
  /** Снять фокус строки геометрии (MVT / deep link), очистить `geometryId` в URL. */
  clearGeometryFocus: () => void;

  activeView: string;
  setActiveView: (view: string) => void;

  cadastralLayersVisible: boolean;
  toggleCadastralLayers: () => void;
  cadastralOpacity: number;
  setCadastralOpacity: (v: number) => void;
  telegramIncidentsVisible: boolean;
  toggleTelegramIncidents: () => void;
  telegramOpacity: number;
  setTelegramOpacity: (v: number) => void;
  workItemsVisible: boolean;
  toggleWorkItemsVisible: () => void;
  workItemsOpacity: number;
  setWorkItemsOpacity: (v: number) => void;
  showWorkItemCreateButton: boolean;
  setShowWorkItemCreateButton: (value: boolean) => void;

  mapSearchQuery: string | null;
  setMapSearchQuery: (query: string | null) => void;
  showBaseLayerSwitcher: boolean;
  setShowBaseLayerSwitcher: (value: boolean) => void;
  /** Кнопка «Экспорт» на карте (вкладка «Слои»). По умолчанию выключена. */
  showMapExportButton: boolean;
  setShowMapExportButton: (value: boolean) => void;
  mvtFilters: MvtFilters;
  setMvtFilters: (filters: MvtFilters) => void;
  mvtLayerSettings: MvtLayerSettings;
  setMvtLayerSettings: (settings: MvtLayerSettings) => void;

  /** Локальные тумблеры справочных оверлеев GeoServer (не серверный ACL). */
  referenceLayerUi: Record<number, ReferenceLayerUiState>;
  setReferenceLayerEnabled: (
    layerId: number,
    enabled: boolean,
    defaultOpacity?: number,
  ) => void;
  setReferenceLayerOpacity: (layerId: number, opacity: number) => void;

  /**
   * Сброс кэша MVT и GeoJSON-кластеров на карте (инкремент epoch в Home).
   * Вызывать после изменения объектов/геометрий на сервере, иначе тайлы и sessionStorage
   * для map-cluster остаются старыми при неизменных фильтрах карты.
   */
  refreshMapVectorTiles: () => void;

  requestEditObject: (object: GeoObject) => void;
  requestEditGeometry: (geometry: any, objectId: number) => void;
  requestStartDrawing: (tool: string, objectId: number) => void;
  /** Приблизить карту к границам геометрии (без тостов) */
  focusGeometryOnMap: (geometry: Geometry) => void;
}

export const MapUiContext = createContext<MapUiContextType | null>(null);

export function useMapUi(): MapUiContextType {
  const ctx = useContext(MapUiContext);
  if (!ctx) {
    throw new Error("useMapUi must be used within MapUiContext.Provider");
  }
  return ctx;
}

export function useMapUiOptional(): MapUiContextType | null {
  return useContext(MapUiContext);
}
